import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DialogContentConsultDialog } from '../models/consult-now/page/consult-content';
import { APIService } from '../Services/main-service.service';
import { APIcalls } from '../Services/api';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { description, keyword } from '../SEO_Constant/seo';
import { capatelize } from '../SEO_Constant/routes.constant';
import { isPlatformBrowser } from '@angular/common';

interface HeroChar {
  ch: string;
  /** Position within the line — drives the stagger delay. */
  i: number;
}
interface HeroWord {
  chars: HeroChar[];
}
interface HeroLine {
  words: HeroWord[];
  grad: boolean;
  /** Character count — lets CSS ramp one gradient across the whole line. */
  n: number;
}
interface Service {
  name: string;
  desc: string;
  link: string;
  icon: string;
  alt: string;
}
interface CommitStat {
  icon: SafeHtml;
  tone: string;
  target: number;
  value: number;
  label: string;
  desc: string;
}
interface CommitFeature {
  icon: SafeHtml;
  title: string;
  sub: string;
}
interface GlobeCountry {
  name: string;
  flag: string;
  /** Position as % of the map stage. */
  x: number;
  y: number;
  since: string;
  /** Which side the persistent card sits on; 'none' = pin + hover tooltip only. */
  card: 'left' | 'right' | 'top' | 'none';
  /** SVG path from headquarters to this pin, in 1000x520 viewBox units. */
  line: string;
}
interface GlobeStat {
  icon: SafeHtml;
  tone: string;
  target: number;
  value: number;
  suffix: string;
  label: string;
}
interface TechCat {
  name: string;
  icon: SafeHtml;
  /** Card centre as % of the stage. */
  x: number;
  y: number;
  /** Curved connection from the hub, in 1000x640 viewBox units. */
  line: string;
  techs: Array<{ name: string; img: string }>;
}
interface Industry {
  name: string;
  /** Swap these paths when the final generated illustrations land in assets. */
  img: string;
  icon: SafeHtml;
  /** Two accent stops driving the card's gradient overlay + glow. */
  a: string;
  b: string;
}
interface TeamMember {
  img: string;
  name: string;
  role: string;
  dept: string;
  desc: string;
  tone: string;
}
interface TeamFeat {
  icon: SafeHtml;
  label: string;
}
interface Testimonial {
  text: string;
  name: string;
  initials: string;
  tone: string;
}
interface HeroFeature {
  title: string;
  sub: string;
  /** Drives the icon tint. */
  tone: 'pink' | 'indigo' | 'purple' | 'cyan';
  icon: SafeHtml;
}

/**
 * Split a headline into words of characters. Characters animate individually,
 * but grouping them into words is what keeps a line from breaking mid-word:
 * each char is an inline-block, so without the word wrapper the browser is
 * free to wrap between any two letters.
 */
function heroLine(text: string, grad = false): HeroLine {
  let i = 0;
  const words = text
    .trim()
    .split(/\s+/)
    .map((word) => ({ chars: [...word].map((ch) => ({ ch, i: i++ })) }));
  return { words, grad, n: i };
}

const SVC_ICON = 'https://images.prismic.io/kurmishadi/';
const SVC_Q = '?auto=compress,format=webp,height=60,width=60';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  // ===================== HERO =====================
  @ViewChild('rsVideo') rsVideo?: ElementRef<HTMLVideoElement>;
  @ViewChildren('rsMagnet') rsMagnets?: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('rsSvc') rsSvc?: ElementRef<HTMLElement>;
  @ViewChild('rsCommit') rsCommit?: ElementRef<HTMLElement>;
  @ViewChild('rsGlobe') rsGlobe?: ElementRef<HTMLElement>;
  @ViewChild('rsCta') rsCta?: ElementRef<HTMLElement>;
  @ViewChild('rsTesti') rsTesti?: ElementRef<HTMLElement>;
  @ViewChild('rsTeam') rsTeam?: ElementRef<HTMLElement>;
  @ViewChild('rsInd') rsInd?: ElementRef<HTMLElement>;
  @ViewChild('rsTech') rsTech?: ElementRef<HTMLElement>;

  heroLines: HeroLine[] = [
    heroLine('We Build'),
    heroLine('Smart Digital'),
    heroLine('Solutions', true),
  ];

  heroFeatures: HeroFeature[] = [];
  commitStats: CommitStat[] = [];
  commitFeatures: CommitFeature[] = [];
  globeCountries: GlobeCountry[] = [];
  globeStats: GlobeStat[] = [];
  testimonials: Testimonial[] = [];
  testiStats: GlobeStat[] = [];
  teamMembers: TeamMember[] = [];
  teamFeats: TeamFeat[] = [];
  industries: Industry[] = [];
  techCats: TechCat[] = [];

  testiConfig = {
    slidesToShow: 3,
    slidesToScroll: 1,
    infinite: true,
    arrows: false,
    dots: true,
    autoplay: true,
    autoplaySpeed: 3500,
    pauseOnHover: true,
    speed: 600,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 640, settings: { slidesToShow: 1 } },
    ],
  };
  hotGlobe = -1;

  services: Service[] = [
    {
      name: 'Web Designing',
      desc: 'Creative and modern designs that make your brand stand out.',
      link: 'service/website-Design',
      icon: SVC_ICON + '0d4f835c-2904-410a-a9a3-6f78aba1d68e_web-design.png' + SVC_Q,
      alt: 'Web Designing',
    },
    {
      name: 'Web Development',
      desc: 'Clean, efficient and scalable web solutions for your business.',
      link: 'service/Web-Application-Development',
      icon: SVC_ICON + 'c4c03523-ced2-46cb-a731-a0f75fb6a2f6_web-development.png' + SVC_Q,
      alt: 'Web-development',
    },
    {
      name: 'E-Commerce Solution',
      desc: 'Powerful online stores that drive sales and growth.',
      link: 'service/EcommerceSolution',
      icon: SVC_ICON + '30f9a5d7-534e-422a-9802-c9ccba1d6d94_bag.png' + SVC_Q,
      alt: 'Bag',
    },
    {
      name: 'Hosting Services',
      desc: 'Fast, secure and reliable hosting for your websites.',
      link: 'service/web-Hosting-Solutions',
      icon: SVC_ICON + '75069fbe-97cc-4b4c-8550-8f08e98d2387_cloud.png' + SVC_Q,
      alt: 'Cloud pic',
    },
    {
      name: 'Digital Marketing',
      desc: 'Result-driven marketing strategies to grow your online presence.',
      link: 'service/digital-Media-Marketing',
      icon: SVC_ICON + '243de374-b89a-4bd7-9c0b-496bae48cdf4_social-media.png' + SVC_Q,
      alt: 'Social-media',
    },
    {
      name: 'SMS Services',
      desc: 'Reach your customers instantly with bulk SMS campaigns.',
      link: 'service/bulkSms',
      icon: SVC_ICON + '3afd070c-0a86-467b-a68a-27891bd892f9_sms.png' + SVC_Q,
      alt: 'service',
    },
    {
      name: 'Website Re-Design',
      desc: 'Refresh your website with modern and user-friendly design.',
      link: 'service/website-Design',
      icon: SVC_ICON + '06ca3a24-a18c-4c65-bb1e-cca1a2a82a3b_monitor.png' + SVC_Q,
      alt: 'Website Designing',
    },
    {
      name: 'Responsive Website',
      desc: 'Mobile-friendly websites that work on every device.',
      link: 'service/website-Design',
      icon: SVC_ICON + '1ade9023-0c81-4ad7-8d61-9f718dd8a755_devices.png' + SVC_Q,
      alt: 'responsive website',
    },
    {
      name: 'App Development',
      desc: 'High-performance mobile apps for Android and iOS.',
      link: 'service/android-app-development',
      icon: SVC_ICON + '26af67ef-fd85-48f6-ae1d-0b9c3006f191_apps.png' + SVC_Q,
      alt: 'Mobile',
    },
    {
      name: 'E-mail Services',
      desc: 'Professional email solutions for your business.',
      link: 'service/bulk-Sms-and-Email-Marketing',
      icon: SVC_ICON + '31fa9ad8-bc17-4d7f-9fa3-fa80f3cfc06b_email_next.png' + SVC_Q,
      alt: 'email',
    },
    {
      name: 'SEO Solution',
      desc: 'Improve your search rankings and get found online.',
      link: 'service/search-Engine-Optimisation',
      icon: SVC_ICON + '6d7714cd-2070-45e5-8423-0bf2838ca2bd_seo.png' + SVC_Q,
      alt: 'seo',
    },
    {
      name: 'Data Entry',
      desc: 'Accurate and reliable data entry services you can trust.',
      link: 'service/DataEntry',
      icon: SVC_ICON + 'b223f690-65a9-42f9-ab64-c84d79418600_check-list.png' + SVC_Q,
      alt: 'data evtry',
    },
  ];

  private heroCleanups: Array<() => void> = [];
  private heroReduced = false;

  // for counter
  conties = 0;
  pojectcomplete = 0;
  projectexpert = 0;
  marketing = 0;
  // counter end
  isBrowser?: boolean;

  countries = [
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW2wiol0Zci9MIQ_australia.webp?auto=compress,format',
      name: 'Australia',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW2uSol0Zci9MIP_canada.webp?auto=compress,format',
      name: 'Canada',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW2wiol0Zci9MIQ_australia.webp?auto=compress,format',
      name: 'Germany',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW2-yol0Zci9MIg_USA.webp?auto=compress,format',
      name: 'United States of America',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW28yol0Zci9MIe_UK.webp?auto=compress,format',
      name: 'United Kingdom',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW27iol0Zci9MIb_UAE.webp?auto=compress,format',
      name: 'United Arab Emirates',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW26Sol0Zci9MIZ_switzerland.webp?auto=compress,format',
      name: 'Switzerland',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW24iol0Zci9MIY_southi-arebia.webp?auto=compress,format',
      name: 'Saudi Arabia',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW22Sol0Zci9MIW_south-africa.webp?auto=compress,format',
      name: 'South Africa',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW20iol0Zci9MIU_netherlands.webp?auto=compress,format',
      name: 'Netherlands',
    },
    {
      flag: 'https://images.prismic.io/kurmishadi/ZkW2ziol0Zci9MIT_Ireland.webp?auto=compress,format',
      name: 'Ireland',
    },
  ];
  locationData: any;

  contriesConfig = {
    slidesToShow: 5,
    slidesToScroll: 1,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 1000,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  };
  formData: FormGroup = new FormGroup({
    Name: new FormControl('', [
      Validators.required,
      Validators.pattern('[a-zA-Z- ]*'),
    ]),
    Email: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
      ),
    ]),
    Number: new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]*'),
      Validators.minLength(10),
      Validators.maxLength(10),
    ]),
    Subject: new FormControl('', [Validators.required]),
    msg: new FormControl('', [Validators.required, Validators.minLength(10)]),
  });

  noSpace(event: any) {
    if (event.keyCode === 32 && !event.target.value) return false;
    return true;
  }

  // form get
  get fullName() {
    return this.formData.get('Name');
  }
  get contact() {
    return this.formData.get('Number');
  }
  get email() {
    return this.formData.get('Email');
  }
  get sub() {
    return this.formData.get('Subject');
  }
  get msg() {
    return this.formData.get('msg');
  }

  // Carousel Content

  CarouselContent = [
    { caption: '', captionmain: '', url: '' },
    { caption: '', captionmain: '', url: '' },
  ];

  // feedback

  clientFeedback = [
    {
      description:
        'The flexibility and capacity of the resilience soft staff has been impressive. Provide best services and have a good customer handling power. This is the best factor.',
      title: 'Mr. Tarun Peswani',
    },
    {
      description:
        'They were very responsive team extremery easy to communicate and work with.The outcome was a flawless website, just the way I wanted.',
      title: 'Mr. Rohit',
    },
    {
      description:
        'The stability provided by resilience soft has help us scale up quickly and efficiently, we can always count on resilience soft. This is the best company ever for web development and designing also.',
      title: 'Meenu Dua',
    },
    {
      description:
        'A great company to work with! Our’s was the firm into mess before seeking their services. Now we are the market leader in terms of price and smooth web option as compare to before.',
      title: 'Aditya Tiwari',
    },
    {
      description:
        'Been a long journey of your support... you made us realise and understand our real market potential...kudos to resilience soft. very passionate about client success.',
      title: 'Mr. Afzal',
    },
    {
      description:
        'A progressive working atmosphere at Resilience Soft is what drove our company towards them. It was exactly what we thought. Happily enjoying client benifits with them.',
      title: 'Abdulla Bata',
    },
    {
      description:
        'The team at Resilience Soft is highly knowledgeable and the best part is that they are more than willing to share their knowledge and expertise. It really worked wonders for us, thanks for all the support.',
      title: 'Chakrdhar Soni',
    },
    {
      description:
        'Better team with sharp skill set, high quality on time delivery and commited bind service remain at the core of Resilience Soft....A true taskmaster and techno expert in its segment.',
      title: 'Mr. Sameer Chaohan',
    },
  ];

  // feedback end

  //FOR RESOURCES
  slides = [
    {
      img: 'https://images.prismic.io/kurmishadi/8da4c8d9-9f53-44b7-9974-2d13098de357_html_css.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/10be0555-8e4d-49a4-9bbc-875a5a16c104_bootstrap.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/9b43e8d6-6df1-4113-b349-6ef48622c307_php.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/4f23bc11-c051-41fc-95ef-551d3949577b_dotnet.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/9b09a745-ba28-41a6-84fe-6b5a6cde03a5_laravel.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/933e2d4e-0298-47d8-bdd3-e367cf61c1ee_wordpress.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/efee81f7-5ece-4852-a89b-72e6720adfa4_MySQL.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/0c596dcc-d313-456b-bf65-73594e7bf3e9_jquery.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/7c7e7164-87a4-4bcd-8571-933a104dc9b6_angular.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/1a890e78-90d6-4931-ba19-7ae7de295216_react.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/e7e59a7b-253c-46f1-bc52-7ea6330f4512_python.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/688a74e8-5cdb-44eb-abac-ecd2442e1d38_node_.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/33249da5-8428-4a89-923a-86344536afe0_mango_db.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ada6e991-5e1e-47bb-a47c-dc2d3e80a266_magento.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/36c7259c-cd2b-419f-9148-99b6fe90c652_android.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/a0694ea8-b2ae-401d-8e66-d1ca233ff6c4_ios-mac.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/10937937-32dd-44b8-9226-434d12cdd4a2_ionic.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/482010bd-30bd-4cd6-b4c2-e68ed9069196_ps.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/20650fe5-265d-4d88-ae34-fc5a930b11ee_pI_copy.png?auto=compress,format=webp,height=83,width=206',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/91cf598c-1758-4bf3-a97c-224c6f608aac_figma.png?auto=compress,format=webp,height=83,width=206',
    },
  ];

  slideConfig = {
    slidesToShow: 6,
    slidesToScroll: 1,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 500,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 500,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  };

  // FOR CLIENTS
  slideConfigmsg = {
    slidesToShow: 3,
    slidesToScroll: 1,
    dots: true,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 1800,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  //end

  // FOR EXPERTS TEAM

  experts = [
    {
      img: 'https://images.prismic.io/kurmishadi/530015ce-677b-4bbb-84d4-d0dc7605bce9_pankaj.jpg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Pankaj Mishra',
      position: 'Director & CEO',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/d81c324c-501f-4d0d-8d41-40ec4e795b5f_prateek.jpg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Prateek Saluja',
      position: 'Director & CEO',
    },
    {
      img: '/assets/images/image/sushant-for-team.webp',
      tittle: 'Sushant Saluja',
      position: 'Functional Manager',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/65de2683-a34a-4e43-8804-a5fef77bdd56_deepak.jpg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Deepak Verma',
      position: 'Manager',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/0fc62679-7b52-41c8-ae81-51d7ec8a01e7_irshad.jpg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Irshad Alam',
      position: 'Web Designer',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/45e2f9ca-80fb-4985-bc7b-86ab530e9db5_ankita.jpeg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Ankita Choudhary',
      position: 'Web Developer (Angular)',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/63828c9d-5c63-4022-a098-2ab1bc80e410_shreya.jpg?auto=compress,format=webp,height=297,width=252',
      tittle: 'Shreya Gupta',
      position: 'Web Developer (PHP)',
    },
  ];

  expertsConfig = {
    slidesToShow: 5,
    slidesToScroll: 1,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 1800,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // end

  // FOR OUR WORKING AREA

  workingAreas = [
    {
      img: 'https://images.prismic.io/kurmishadi/ad6219d6-62f4-4989-8139-1eb9e92924db_e-commerce.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/a437696a-6db0-4f35-82e5-ecb76e7d60df_educations.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/c091bb32-16bb-4acb-b47a-e580731f216f_grocery.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/0a2bbb65-1071-4534-a17d-07f33d4072e0_transport.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/a0330572-d8d8-4d9a-9ca7-8d0b4443bb9d_medical.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
    {
      img: 'https://images.prismic.io/kurmishadi/ac6483a8-b6f8-452a-befe-19f5862ada41_payments.jpg?auto=compress,format=webp,height=283,width=283',
      member_feedback: '',
    },
  ];

  areasConfig = {
    slidesToShow: 4,
    slidesToScroll: 1,
    infinite: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 1800,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };
  isadd?: boolean;

  // end
  constructor(
    private title: Title,
    private meta: Meta,
    public actRoute: ActivatedRoute,
    private api: APIService,
    private zone: NgZone,
    private dialog: MatDialog,
    sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.heroFeatures = this.buildFeatures(sanitizer);
    this.commitStats = this.buildCommitStats(sanitizer);
    this.commitFeatures = this.buildCommitFeatures(sanitizer);
    this.globeCountries = this.buildGlobeCountries();
    this.globeStats = this.buildGlobeStats(sanitizer);
    this.buildTestimonials(sanitizer);
    this.buildTeam(sanitizer);
    this.buildIndustries(sanitizer);
    this.buildTechCats(sanitizer);
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      const contrycount = setInterval(() => {
        if (this.isadd) {
          this.conties++;
          if (this.conties == 20) {
            clearInterval(contrycount);
          }
        }
      }, 50);

      const projectcount = setInterval(() => {
        if (this.isadd) {
          this.pojectcomplete++;
          if (this.pojectcomplete == 200) {
            clearInterval(projectcount);
          }
        }
      }, 1);

      const projectexpertcount = setInterval(() => {
        if (this.isadd) {
          this.projectexpert++;
          if (this.projectexpert == 25) {
            clearInterval(projectexpertcount);
          }
        }
      }, 45);

      const marketingcount = setInterval(() => {
        if (this.isadd) {
          this.marketing++;
          if (this.marketing == 10) {
            clearInterval(marketingcount);
          }
        }
      }, 70);
    }
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.title.setTitle(
      'Top Website Development Company In India | Resiliencesoft'
    );
    this.meta.removeTag('name=description');
    this.meta.removeTag('name=keywords');
    this.meta.addTags([
      {
        name: 'description',
        content: description.Home,
      },
      {
        name: 'keywords',
        content: capatelize(keyword.globle) + capatelize(keyword.Home),
      },
    ]);
    // this.api.getData(APIcalls.getApi.Employee).subscribe((res) => {res});
  }

  @ViewChild('myDOMElement', { static: true }) MyDOMElement?: ElementRef;

  @HostListener('window:scroll', ['$event'])
  isScrolledIntoView() {
    if (this.MyDOMElement) {
      const rect = this.MyDOMElement.nativeElement.getBoundingClientRect();
      const topShown = rect.top < window.innerHeight;
      const bottomShown = rect.bottom >= 60;
      this.isadd = topShown && bottomShown;
      // console.log(rect, topShown, bottomShown, this.isadd)
    }
    if (this.isadd) {
    }
  }

  // ==================================================================
  //  HERO — looping background video + CTA interactions
  // ==================================================================

  private svg(sanitizer: DomSanitizer, path: string): SafeHtml {
    return sanitizer.bypassSecurityTrustHtml(
      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.7" stroke-linecap="round"
            stroke-linejoin="round">${path}</svg>`
    );
  }

  private buildCommitStats(sanitizer: DomSanitizer): CommitStat[] {
    const s = (p: string) => this.svg(sanitizer, p);
    return [
      {
        icon: s('<path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 12l10 5 10-5"/><path d="M2 17l10 5 10-5"/>'),
        tone: 'var(--c-pink)',
        target: 20,
        value: 20,
        label: 'Countries Served',
        desc: 'We have worked with clients from 20+ countries worldwide.',
      },
      {
        icon: s('<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z"/><path d="m9 11 2 2 4-4"/>'),
        tone: 'var(--c-purple)',
        target: 200,
        value: 200,
        label: 'Project Completed',
        desc: 'Successfully delivered 200+ projects across industries.',
      },
      {
        icon: s('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="m15.5 10.5 1.6 1.6 3.4-3.4"/>'),
        tone: 'var(--c-indigo)',
        target: 25,
        value: 25,
        label: 'Project Experts',
        desc: 'Our team of 25+ experts builds amazing digital solutions.',
      },
      {
        icon: s('<rect x="4" y="12" width="3" height="6" rx="1"/><rect x="10.5" y="8" width="3" height="10" rx="1"/><rect x="17" y="4.5" width="3" height="13.5" rx="1"/><path d="M4 4.5l6 4 4-3 6-3"/>'),
        tone: 'var(--c-blue)',
        target: 10,
        value: 10,
        label: 'Market Experience',
        desc: '10+ years of experience delivering value and driving growth.',
      },
    ];
  }

  private buildCommitFeatures(sanitizer: DomSanitizer): CommitFeature[] {
    const s = (p: string) => this.svg(sanitizer, p);
    return [
      {
        icon: s('<path d="M12 3 4.5 6v6c0 4.5 3.2 8.3 7.5 9.5 4.3-1.2 7.5-5 7.5-9.5V6z"/><path d="m9 12 2 2 4-4"/>'),
        title: 'Quality First',
        sub: 'We never compromise with quality.',
      },
      {
        icon: s('<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.4"/>'),
        title: 'Client Focused',
        sub: 'Your success is our priority.',
      },
      {
        icon: s('<path d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2c.9-.9.9-2.3 0-3.2a2.3 2.3 0 0 0-3 .2z"/><path d="M12.5 12.5 9 9c1-4 4-7.5 9-8 .5 5-3 8-8 9z"/><path d="M14.5 6.5h.01"/>'),
        title: 'Result Driven',
        sub: 'We deliver results that matter.',
      },
    ];
  }

  /**
   * Reveal the commitment section on scroll and count its stats up.
   *
   * Stat values are seeded at their targets (so SSR / no-JS shows real numbers),
   * and only reset to 0 at the moment the count-up actually starts. Cards render
   * by default; `js-anim` opts them into the hidden start state, so a broken
   * observer degrades to "no animation", never "no content".
   */
  private revealCommit(): void {
    const el = this.rsCommit?.nativeElement;
    if (!el) return;

    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }

    el.classList.add('js-anim');

    const run = () => {
      el.classList.add('is-in');
      this.countUpCommit();
    };

    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      run();
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          run();
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }

    this.bindCommitParallax(el);
  }

  private countUpCommit(): void {
    const duration = 1500;
    this.commitStats.forEach((stat, i) => {
      stat.value = 0;
      const begin = performance.now() + i * 120;
      const step = (now: number) => {
        if (now < begin) {
          requestAnimationFrame(step);
          return;
        }
        const t = Math.min(1, (now - begin) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        const v = Math.round(stat.target * eased);
        if (v !== stat.value) this.zone.run(() => (stat.value = v));
        if (t < 1) requestAnimationFrame(step);
      };
      this.zone.runOutsideAngular(() => requestAnimationFrame(step));
    });
  }

  private bindCommitParallax(el: HTMLElement): void {
    let queued = false;
    let x = 0,
      y = 0;
    const apply = () => {
      queued = false;
      el.style.setProperty('--px', x.toFixed(4));
      el.style.setProperty('--py', y.toFixed(4));
    };
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      x = (e.clientX - r.left) / r.width - 0.5;
      y = (e.clientY - r.top) / r.height - 0.5;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };
    const onLeave = () => {
      x = 0;
      y = 0;
      if (!queued) {
        queued = true;
        requestAnimationFrame(apply);
      }
    };
    this.zone.runOutsideAngular(() => {
      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });
    this.heroCleanups.push(() => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    });
  }

  // ------------------------------------------------------------
  //  GLOBAL PARTNERS — world map
  // ------------------------------------------------------------

  /** Headquarters position in the map's 1000x520 viewBox. */
  private static readonly HQ = { x: 670, y: 260 };

  /**
   * Quadratic curve from HQ to a pin, bowing away from the straight line so
   * the connections read as arcs rather than spokes.
   */
  private globeCurve(xPct: number, yPct: number): string {
    const { x: hx, y: hy } = HomeComponent.HQ;
    const px = xPct * 10;
    const py = yPct * 5.2;
    const dx = px - hx;
    const dy = py - hy;
    const dist = Math.hypot(dx, dy) || 1;
    const lift = Math.min(85, dist * 0.22);
    let cx = (hx + px) / 2 - (-dy / dist) * lift;
    let cy = (hy + py) / 2 - (dx / dist) * lift;
    if (cy > (hy + py) / 2) {
      cx = (hx + px) / 2 + (-dy / dist) * lift;
      cy = (hy + py) / 2 + (dx / dist) * lift;
    }
    return `M${hx},${hy} Q${cx.toFixed(0)},${cy.toFixed(0)} ${px.toFixed(0)},${py.toFixed(0)}`;
  }

  private buildGlobeCountries(): GlobeCountry[] {
    const F = 'https://images.prismic.io/kurmishadi/';
    const Q = '?auto=compress,format';
    const rows: Array<
      [string, string, number, number, string, GlobeCountry['card']]
    > = [
      ['United States', 'ZkW2-yol0Zci9MIg_USA.webp', 19, 36, '2021', 'left'],
      ['Canada', 'ZkW2uSol0Zci9MIP_canada.webp', 15, 20, '2021', 'top'],
      ['United Kingdom', 'ZkW28yol0Zci9MIe_UK.webp', 46, 24.5, '2020', 'top'],
      ['Ireland', 'ZkW2ziol0Zci9MIT_Ireland.webp', 43.2, 27.5, '2022', 'none'],
      ['Netherlands', 'ZkW20iol0Zci9MIU_netherlands.webp', 48.4, 25.5, '2020', 'none'],
      ['Germany', 'ZkW2wiol0Zci9MIQ_australia.webp', 50.4, 28.5, '2022', 'none'],
      ['Switzerland', 'ZkW26Sol0Zci9MIZ_switzerland.webp', 48.6, 33, '2021', 'left'],
      ['Saudi Arabia', 'ZkW24iol0Zci9MIY_southi-arebia.webp', 57, 52, '2020', 'left'],
      ['United Arab Emirates', 'ZkW27iol0Zci9MIb_UAE.webp', 62, 44, '2019', 'top'],
      ['South Africa', 'ZkW22Sol0Zci9MIW_south-africa.webp', 52.5, 76, '2021', 'left'],
      ['Australia', 'ZkW2wiol0Zci9MIQ_australia.webp', 83, 79, '2022', 'top'],
    ];
    return rows.map(([name, flag, x, y, since, card]) => ({
      name,
      flag: F + flag + Q,
      x,
      y,
      since,
      card,
      line: this.globeCurve(x, y),
    }));
  }

  private buildGlobeStats(sanitizer: DomSanitizer): GlobeStat[] {
    const s = (p: string) => this.svg(sanitizer, p);
    return [
      {
        icon: s('<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/>'),
        tone: 'var(--g-pink)',
        target: 20,
        value: 20,
        suffix: '+',
        label: 'Countries',
      },
      {
        icon: s('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
        tone: 'var(--g-purple)',
        target: 200,
        value: 200,
        suffix: '+',
        label: 'Happy Clients',
      },
      {
        icon: s('<rect x="2.5" y="7" width="19" height="13" rx="2"/><path d="M8.5 7V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2"/>'),
        tone: 'var(--g-indigo)',
        target: 500,
        value: 500,
        suffix: '+',
        label: 'Projects Delivered',
      },
      {
        icon: s('<path d="M19.8 5.6a5 5 0 0 0-7.1 0L12 6.3l-.7-.7a5 5 0 1 0-7.1 7.1l.7.7 7.1 7 7.1-7 .7-.7a5 5 0 0 0 0-7.1z"/>'),
        tone: 'var(--g-blue)',
        target: 98,
        value: 98,
        suffix: '%',
        label: 'Client Satisfaction',
      },
    ];
  }

  /** Same fail-safe reveal pattern as the other sections. */
  private revealGlobe(): void {
    const el = this.rsGlobe?.nativeElement;
    if (!el) return;

    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }

    el.classList.add('js-anim');

    const run = () => {
      el.classList.add('is-in');
      this.countUpGlobe();
    };

    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      run();
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          run();
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }

    this.bindCommitParallax(el);
  }

  /** Contact CTA: reveal + parallax only — no counters here. */
  private revealCta(): void {
    const el = this.rsCta?.nativeElement;
    if (!el) return;
    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }
    el.classList.add('js-anim');
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      el.classList.add('is-in');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          el.classList.add('is-in');
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }
    this.bindCommitParallax(el);
  }

  /**
   * Testimonial cards derive from the untouched clientFeedback array — same
   * eight reviews, same order. Only presentation data (initials, tint) is
   * computed here.
   */
  private buildTestimonials(sanitizer: DomSanitizer): void {
    const tones = ['#ff2d95', '#b14eff', '#6c63ff', '#3b82f6'];
    const initials = (name: string) => {
      const words = name
        .split(/\s+/)
        .filter((w) => !/^(mr|mrs|ms|dr)\.?$/i.test(w));
      return words
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    };
    this.testimonials = this.clientFeedback.map((f, i) => ({
      text: f.description,
      name: f.title,
      initials: initials(f.title),
      tone: tones[i % tones.length],
    }));

    const svg = (p: string) => this.svg(sanitizer, p);
    this.testiStats = [
      {
        icon: svg('<circle cx="12" cy="12" r="9"/><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0"/><path d="M9 9.5h.01M15 9.5h.01"/>'),
        tone: 'var(--ts-pink)',
        target: 98,
        value: 98,
        suffix: '%',
        label: 'Client Satisfaction',
      },
      {
        icon: svg('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
        tone: 'var(--ts-purple)',
        target: 200,
        value: 200,
        suffix: '+',
        label: 'Happy Clients',
      },
      {
        icon: svg('<rect x="2.5" y="7" width="19" height="13" rx="2"/><path d="M8.5 7V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2"/>'),
        tone: 'var(--ts-indigo)',
        target: 500,
        value: 500,
        suffix: '+',
        label: 'Projects Delivered',
      },
      {
        icon: svg('<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/>'),
        tone: 'var(--ts-blue)',
        target: 20,
        value: 20,
        suffix: '+',
        label: 'Countries Served',
      },
    ];
  }

  private revealTesti(): void {
    const el = this.rsTesti?.nativeElement;
    if (!el) return;
    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }
    el.classList.add('js-anim');
    const run = () => {
      el.classList.add('is-in');
      this.countUpTesti();
    };
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      run();
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          run();
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }
    this.bindCommitParallax(el);
  }

  /**
   * Team cards derive from the untouched `experts` array — same seven people,
   * photos, names and positions. Departments are derived from the position;
   * descriptions are role blurbs (editable here), not personal claims.
   */
  private buildTeam(sanitizer: DomSanitizer): void {
    const tones = ['#ff2d95', '#b14eff', '#6c63ff', '#3b82f6'];
    const dept = (pos: string) =>
      /director|ceo/i.test(pos)
        ? 'Leadership'
        : /manager/i.test(pos)
        ? 'Management'
        : /design/i.test(pos)
        ? 'Design'
        : 'Development';
    const blurbs: Record<string, string> = {
      'Pankaj Mishra':
        'Guides the company vision and strategy, driving growth and lasting client success.',
      'Prateek Saluja':
        'Shapes direction and partnerships, ensuring every project delivers real value.',
      'Sushant Saluja':
        'Leads cross-functional teams to deliver robust business solutions with efficiency.',
      'Deepak Verma':
        'A strategic thinker with expertise in project management and team collaboration.',
      'Irshad Alam':
        'Creative designer with a strong eye for detail, crafting user-friendly digital experiences.',
      'Ankita Choudhary':
        'Angular specialist focused on building dynamic, responsive, high-performance web applications.',
      'Shreya Gupta':
        'Skilled in core PHP development and backend logic, ensuring secure and scalable solutions.',
    };
    this.teamMembers = this.experts.map((e, i) => ({
      img: e.img,
      name: e.tittle,
      role: e.position,
      dept: dept(e.position),
      desc: blurbs[e.tittle] || '',
      tone: tones[i % tones.length],
    }));

    const svg = (p: string) => this.svg(sanitizer, p);
    this.teamFeats = [
      {
        icon: svg('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
        label: 'Experienced Professionals',
      },
      {
        icon: svg('<path d="M12 3 4.5 6v6c0 4.5 3.2 8.3 7.5 9.5 4.3-1.2 7.5-5 7.5-9.5V6z"/><path d="m9 12 2 2 4-4"/>'),
        label: 'Client-First Approach',
      },
      {
        icon: svg('<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.5 1 2.5h6c0-1 .4-1.9 1-2.5A6 6 0 0 0 12 3z"/>'),
        label: 'Innovation Driven',
      },
      {
        icon: svg('<path d="M21 12a9 9 0 1 1-9-9"/><path d="M21 3v6h-6"/><path d="m21 3-9 9"/>'),
        label: 'Agile Development',
      },
      {
        icon: svg('<circle cx="12" cy="8" r="5.5"/><path d="m8.5 12.5-2 8 5.5-3 5.5 3-2-8"/>'),
        label: 'Quality Focused',
      },
    ];
  }

  private buildIndustries(sanitizer: DomSanitizer): void {
    const svg = (p: string) => this.svg(sanitizer, p);
    this.industries = [
      {
        name: 'Education',
        img: 'assets/images/education_home.png',
        icon: svg('<path d="m12 4-10 5 10 5 10-5z"/><path d="M6 11.5V16c0 1.5 2.7 3 6 3s6-1.5 6-3v-4.5"/><path d="M22 9v5"/>'),
        a: '#2563eb',
        b: '#38bdf8',
      },
      {
        name: 'Online Grocery',
        img: 'assets/images/grocery_home.png',
        icon: svg('<path d="M5.5 8h13l-1 12.5a2 2 0 0 1-2 1.5h-7a2 2 0 0 1-2-1.5z"/><path d="M8.5 10V6.5a3.5 3.5 0 0 1 7 0V10"/>'),
        a: '#ff2d95',
        b: '#ff6a5f',
      },
      {
        name: 'Transport',
        img: 'assets/images/transport_home.png',
        icon: svg('<path d="M2.5 7h11v10h-11z"/><path d="M13.5 10h4l3 3v4h-7"/><circle cx="6.5" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/>'),
        a: '#b14eff',
        b: '#6c63ff',
      },
      {
        name: 'Medical',
        img: 'assets/images/medical_home.png',
        icon: svg('<path d="M12 4.5v15M4.5 12h15"/><circle cx="12" cy="12" r="9.5"/>'),
        a: '#0ea977',
        b: '#2dd4bf',
      },
    ];
  }

  private buildTechCats(sanitizer: DomSanitizer): void {
    const svg = (p: string) => this.svg(sanitizer, p);
    const D = 'https://cdn.jsdelivr.net/gh/devicons/devicon@v2.16.0/icons/';
    const t = (name: string, path: string) => ({ name, img: D + path + '.svg' });
    this.techCats = [
      {
        name: 'Frontend',
        icon: svg('<path d="m8 6-6 6 6 6"/><path d="m16 6 6 6-6 6"/>'),
        x: 50,
        y: 15,
        line: 'M500,300 C500,250 500,200 500,130',
        techs: [
          t('Angular', 'angular/angular-original'),
          t('React', 'react/react-original'),
          t('Vue.js', 'vuejs/vuejs-original'),
        ],
      },
      {
        name: 'Backend',
        icon: svg('<rect x="3" y="4" width="18" height="7" rx="2"/><rect x="3" y="13" width="18" height="7" rx="2"/><path d="M7 7.5h.01M7 16.5h.01"/>'),
        x: 17,
        y: 35,
        line: 'M500,300 C410,285 320,265 235,235',
        techs: [
          t('Python', 'python/python-original'),
          t('Node.js', 'nodejs/nodejs-original'),
          t('.NET', 'dot-net/dot-net-original'),
        ],
      },
      {
        name: 'Database',
        icon: svg('<ellipse cx="12" cy="5.5" rx="7.5" ry="3"/><path d="M4.5 5.5v13c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-13"/><path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3"/>'),
        x: 83,
        y: 35,
        line: 'M500,300 C590,285 680,265 765,235',
        techs: [
          t('MySQL', 'mysql/mysql-original'),
          t('MongoDB', 'mongodb/mongodb-original'),
          t('PostgreSQL', 'postgresql/postgresql-original'),
        ],
      },
      {
        name: 'Cloud & DevOps',
        icon: svg('<path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.2 9.5 4 4 0 0 0 6.5 19z"/>'),
        x: 17,
        y: 71,
        line: 'M500,300 C410,330 320,380 235,440',
        techs: [
          t('AWS', 'amazonwebservices/amazonwebservices-plain-wordmark'),
          t('Docker', 'docker/docker-original'),
          t('Firebase', 'firebase/firebase-plain'),
        ],
      },
      {
        name: 'Tools & Others',
        icon: svg('<path d="M14.7 6.3a4.5 4.5 0 0 0-6 6L3 18l3 3 5.7-5.7a4.5 4.5 0 0 0 6-6L14 13l-3-3z"/>'),
        x: 83,
        y: 71,
        line: 'M500,300 C590,330 680,380 765,440',
        techs: [
          t('Git', 'git/git-original'),
          t('GitHub', 'github/github-original'),
          t('VS Code', 'vscode/vscode-original'),
        ],
      },
      {
        name: 'Mobile',
        icon: svg('<rect x="6.5" y="2.5" width="11" height="19" rx="2.5"/><path d="M11 18.5h2"/>'),
        x: 50,
        y: 85,
        line: 'M500,300 C500,370 500,430 500,520',
        techs: [
          t('Flutter', 'flutter/flutter-original'),
          t('React Native', 'react/react-original'),
          t('Ionic', 'ionic/ionic-original'),
        ],
      },
    ];
  }

  private revealTech(): void {
    const el = this.rsTech?.nativeElement;
    if (!el) return;
    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }
    el.classList.add('js-anim');
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      el.classList.add('is-in');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          el.classList.add('is-in');
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }
    this.bindCommitParallax(el);
  }

  private revealInd(): void {
    const el = this.rsInd?.nativeElement;
    if (!el) return;
    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }
    el.classList.add('js-anim');
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      el.classList.add('is-in');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          el.classList.add('is-in');
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }
    this.bindCommitParallax(el);
  }

  private revealTeam(): void {
    const el = this.rsTeam?.nativeElement;
    if (!el) return;
    if (this.heroReduced) {
      el.classList.add('is-in');
      return;
    }
    el.classList.add('js-anim');
    if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      el.classList.add('is-in');
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          el.classList.add('is-in');
          io.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      io.observe(el);
      this.heroCleanups.push(() => io.disconnect());
    }
    this.bindCommitParallax(el);
  }

  private countUpTesti(): void {
    const duration = 1500;
    this.testiStats.forEach((stat, i) => {
      stat.value = 0;
      const begin = performance.now() + i * 120;
      const step = (now: number) => {
        if (now < begin) {
          requestAnimationFrame(step);
          return;
        }
        const t = Math.min(1, (now - begin) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const v = Math.round(stat.target * eased);
        if (v !== stat.value) this.zone.run(() => (stat.value = v));
        if (t < 1) requestAnimationFrame(step);
      };
      this.zone.runOutsideAngular(() => requestAnimationFrame(step));
    });
  }

  private countUpGlobe(): void {
    const duration = 1500;
    this.globeStats.forEach((stat, i) => {
      stat.value = 0;
      const begin = performance.now() + i * 120;
      const step = (now: number) => {
        if (now < begin) {
          requestAnimationFrame(step);
          return;
        }
        const t = Math.min(1, (now - begin) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const v = Math.round(stat.target * eased);
        if (v !== stat.value) this.zone.run(() => (stat.value = v));
        if (t < 1) requestAnimationFrame(step);
      };
      this.zone.runOutsideAngular(() => requestAnimationFrame(step));
    });
  }

  private buildFeatures(sanitizer: DomSanitizer): HeroFeature[] {
    const svg = (path: string) =>
      sanitizer.bypassSecurityTrustHtml(
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.7" stroke-linecap="round"
              stroke-linejoin="round">${path}</svg>`
      );
    return [
      {
        title: 'Innovative',
        sub: 'Solutions',
        tone: 'pink',
        icon: svg(
          '<path d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2c.9-.9.9-2.3 0-3.2a2.3 2.3 0 0 0-3 .2z"/><path d="M12.5 12.5 9 9c1-4 4-7.5 9-8 .5 5-3 8-8 9z"/><path d="M14.5 6.5h.01"/>'
        ),
      },
      {
        title: 'Custom',
        sub: 'Development',
        tone: 'indigo',
        icon: svg('<path d="m8 6-6 6 6 6"/><path d="m16 6 6 6-6 6"/>'),
      },
      {
        title: 'Growth',
        sub: 'Focused',
        tone: 'purple',
        icon: svg('<path d="M3 20V10M9 20V4M15 20v-7M21 20V8"/>'),
      },
      {
        title: 'Reliable',
        sub: 'Support',
        tone: 'cyan',
        icon: svg(
          '<path d="M12 3 4.5 6v6c0 4.5 3.2 8.3 7.5 9.5 4.3-1.2 7.5-5 7.5-9.5V6z"/><path d="m9 12 2 2 4-4"/>'
        ),
      },
    ];
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.heroReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.setupVideo();
    this.revealServices();
    this.revealCommit();
    this.revealGlobe();
    this.revealCta();
    this.revealTesti();
    this.revealTeam();
    this.revealInd();
    this.revealTech();
    // the counters are driven by a scroll handler, but the stats now sit in the
    // hero — evaluate once so they run without waiting for the first scroll
    this.isScrolledIntoView();
    if (!this.heroReduced) {
      this.bindMagnets();
      this.bindServiceParallax();
    }
  }

  ngOnDestroy(): void {
    this.heroCleanups.forEach((fn) => fn());
    this.heroCleanups = [];
  }

  /**
   * The video is decorative and has no controls, so all this does is honour
   * reduced-motion and retry once if the browser blocks the autoplay attribute.
   */
  private setupVideo(): void {
    const video = this.rsVideo?.nativeElement;
    if (!video) return;

    if (this.heroReduced) {
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }
    this.zone.runOutsideAngular(() => {
      video.play().catch(() => undefined);
    });
  }

  /**
   * Reveal the services grid on scroll.
   *
   * The cards are visible by default in CSS; `js-anim` is what opts them into
   * starting hidden. That ordering matters — if this method never runs, or the
   * observer never fires, the grid still renders rather than staying invisible.
   */
  private revealServices(): void {
    const el = this.rsSvc?.nativeElement;
    if (!el) return;

    const show = () => el.classList.add('is-in');
    if (this.heroReduced) {
      show();
      return;
    }

    el.classList.add('js-anim');

    // already on screen at load: reveal now, no observer needed
    if (el.getBoundingClientRect().top < window.innerHeight * 0.88) {
      show();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        show();
        io.disconnect();
      },
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );
    io.observe(el);
    this.heroCleanups.push(() => io.disconnect());
  }

  /** Mouse parallax for the decorative shapes behind the services grid. */
  private bindServiceParallax(): void {
    const el = this.rsSvc?.nativeElement;
    if (!el) return;

    let queued = false;
    let x = 0,
      y = 0;
    const apply = () => {
      queued = false;
      el.style.setProperty('--px', x.toFixed(4));
      el.style.setProperty('--py', y.toFixed(4));
    };
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      x = (e.clientX - r.left) / r.width - 0.5;
      y = (e.clientY - r.top) / r.height - 0.5;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };
    const onLeave = () => {
      x = 0;
      y = 0;
      if (!queued) {
        queued = true;
        requestAnimationFrame(apply);
      }
    };

    this.zone.runOutsideAngular(() => {
      el.addEventListener('mousemove', onMove, { passive: true });
      el.addEventListener('mouseleave', onLeave, { passive: true });
    });
    this.heroCleanups.push(() => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    });
  }

  /** Magnetic pull on the CTA buttons. */
  private bindMagnets(): void {
    this.rsMagnets?.forEach(({ nativeElement: el }) => {
      const onMove = (e: MouseEvent) => {
        const r = el.getBoundingClientRect();
        const mx = e.clientX - r.left - r.width / 2;
        const my = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${mx * 0.22}px, ${my * 0.3}px) scale(1.04)`;
      };
      const onLeave = () => (el.style.transform = 'translate(0,0) scale(1)');

      this.zone.runOutsideAngular(() => {
        el.addEventListener('mousemove', onMove, { passive: true });
        el.addEventListener('mouseleave', onLeave, { passive: true });
      });
      this.heroCleanups.push(() => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      });
    });
  }

  /** Click ripple. */
  ripple(event: MouseEvent): void {
    if (!this.isBrowser || this.heroReduced) return;
    const btn = event.currentTarget as HTMLElement;
    const r = btn.getBoundingClientRect();
    const size = Math.max(r.width, r.height) * 2.2;

    const span = document.createElement('span');
    span.className = 'rs-ripple';
    span.style.width = span.style.height = `${size}px`;
    span.style.left = `${event.clientX - r.left}px`;
    span.style.top = `${event.clientY - r.top}px`;
    btn.appendChild(span);
    span.addEventListener('animationend', () => span.remove());
  }

  /** Primary CTA — ripple, then open the existing consultation dialog. */
  rippleAndConsult(event: MouseEvent): void {
    this.ripple(event);
    this.dialog.open(DialogContentConsultDialog);
    try {
      localStorage.setItem('Dialogopen', 'welcomedialog');
    } catch {
      /* storage unavailable — dialog still opens */
    }
  }

  submit() {
    const formData = new FormData();
    formData.append('name', this.formData.get('Name')?.value);
    formData.append('email', this.formData.get('Email')?.value);
    formData.append('phone', this.formData.get('Number')?.value);
    formData.append('subject', this.formData.get('Subject')?.value);
    formData.append('message', this.formData.get('msg')?.value);
    this.formData.reset();
    this.api.postData(APIcalls.mailApi.Contact, formData).subscribe({
      next: (res: any) => {
        this.api.openSnackBar(
          ' Form Has Been Successfully Submitted, We Will Contact You Soon ',
          5
        );
      },
      error: () => {
        this.api.openSnackBar(
          " We Can't Proceed Your Request at This Time ",
          2
        );
      },
    });
  }
}
